import "./styles.scss";

import { Fragment, useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import queryString from "query-string";
import classnames from "classnames";

import useResponsive from "libs/helpers/useResponsive";
import useCloseOnKey from "hooks/useCloseOnKey";
import {
  useHighlight,
  useTaskOnboardingRender,
  task_onboarding_action,
} from "./hooks";

const mainClass = "task-onboarding";

const TaskOnboarding = ({
  init_step,
  task_id,
  task_status,
  onClose,
  validate,
  setExplanation,
}) => {
  const { t } = useTranslation();
  const { search } = useLocation();
  const query = queryString.parse(search);
  const { max } = useResponsive();

  const [step, setStep] = useState(init_step || 0);

  const current_step = useTaskOnboardingRender(
    step,
    setStep,
    onClose,
    query?.referrer,
    validate,
    task_id,
    task_status,
  );

  useCloseOnKey(onClose, undefined, step < 10);

  const ref = useRef(null);

  useEffect(() => {
    const element = ref.current;

    const handleScroll = (e) => {
      const hasOverflow = max.lg
        ? document.documentElement.scrollHeight > window.innerHeight
        : document.body.scrollHeight > window.innerHeight;

      if (hasOverflow) {
        e.preventDefault();

        const delta = e.deltaY || e.detail || -e.wheelDelta;
        const scrollSpeed = 2;
        const scrollAmount = delta * scrollSpeed;

        if (max.lg) {
          document.documentElement.scrollTo({
            top: document.documentElement.scrollTop + scrollAmount,
            behavior: "smooth",
          });
        } else {
          document.body.scrollTo({
            top: document.body.scrollTop + scrollAmount,
            behavior: "smooth",
          });
        }
      }
    };

    if (element) {
      element.addEventListener("wheel", handleScroll, { passive: false });
      element.addEventListener("mousewheel", handleScroll, { passive: false });
      element.addEventListener("DOMMouseScroll", handleScroll, {
        passive: false,
      });
    }

    return () => {
      if (element) {
        element.removeEventListener("wheel", handleScroll);
        element.removeEventListener("mousewheel", handleScroll);
        element.removeEventListener("DOMMouseScroll", handleScroll);
      }
    };
  }, [max.lg]);

  const highlight = useHighlight(current_step?.element, step);

  return (
    <div className={mainClass} ref={ref}>
      <div className={`${mainClass}__highlight`} />
      <div
        className={classnames(`${mainClass}__tooltip`, { visible: highlight })}
      >
        <div className={`${mainClass}__tooltip__content`}>
          <div className={`${mainClass}__tooltip__content__header`}>
            <div className={`${mainClass}__tooltip__content__dots`}>
              <ul>
                {new Array(11).fill().map((_, index) => (
                  <li key={index}>
                    <span
                      className={classnames({
                        active: index === step,
                        visited: index <= step,
                      })}
                    />
                  </li>
                ))}
              </ul>
              <p>{step + 1}/11</p>
            </div>
            {step < 10 && (
              <button type="button" onClick={onClose}>
                {t("Close guide")}
              </button>
            )}
          </div>
          <div className={`${mainClass}__tooltip__content__description`}>
            {current_step?.description?.map((item, index) => {
              const isButton = item?.type === "button";
              const Tag = isButton ? "button" : Fragment;

              return (
                <Tag
                  key={index}
                  {...(isButton
                    ? {
                        onClick: () =>
                          task_onboarding_action(item?.action, setExplanation),
                      }
                    : {})}
                >
                  {t(item?.content).replaceAll("'", '"')}{" "}
                </Tag>
              );
            })}
          </div>
          {step > 0 && (
            <div className={`${mainClass}__tooltip__content__actions`}>
              {current_step?.buttons?.map((item, index) =>
                item?.label ? (
                  item?.onClick ? (
                    <button
                      key={index}
                      onClick={
                        item?.onClick
                          ? () => item.onClick({ last: step === 10 })
                          : undefined
                      }
                    >
                      {t(item.label)}
                    </button>
                  ) : (
                    <Link key={index} to={item?.href}>
                      {t(item.label)}
                    </Link>
                  )
                ) : (
                  <div key={index} />
                ),
              )}
            </div>
          )}
        </div>
      </div>
      <div className={`${mainClass}__backdrop`} />
    </div>
  );
};

export default TaskOnboarding;
